/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Typography,
  Divider,
  CircularProgress,
  Paper,
  Chip,
  Stack,
  Button as MDButton,
  Autocomplete,
  TextField,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import Button from "components/generals/Button";
import {
  FilterList,
  CalendarToday,
  DateRange,
  Today,
  Schedule,
  Clear,
  Print,
  Close,
} from "@mui/icons-material";
import DatePicker from "components/generals/DatePicker";
import { AccountingMovementInterface, DateTypeFiltersInterface, HeaderEnum } from "medicalsystem-shared";
import { QueryResultInterface, SearchParamsInterface } from "medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { createSafeDate, formatDateForAPI } from "utils/date.utils";
import { DateFilterTypeEnum, MonthEnum } from "medicalsystem-shared";

/**
 * Properties for the PrintFiltersModal component.
 * 
 * @interface PrintFiltersModalProps
 * @property {boolean} open - Whether modal is visible
 * @property {Function} onClose - Callback when modal is closed
 * @property {Function} onApplyFilters - Callback with applied filters and filtered movements
 * @property {AccountingMovementInterface[]} movements - All available movements
 * @property {string} accountingId - Patient accounting ID for server filtering
 * @property {Function} t - Translation function for i18n
 */
interface PrintFiltersModalProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: DateTypeFiltersInterface, filteredMovements: AccountingMovementInterface[]) => void;
  movements: AccountingMovementInterface[];
  accountingId: string;
  t: (key: string) => string;
}

/**
 * Print filters modal component for accounting movements.
 * Provides advanced date filtering options before printing account statements.
 * 
 * Features:
 * - Multiple filter types: All, Specific Date, Month, Year, Date Range
 * - Date picker integration
 * - Month and year dropdowns
 * - Real-time filter application
 * - Loading state during filter fetch
 * - Visual filter summary with chips
 * - Clear filters functionality
 * - Print and apply actions
 * - Responsive Material-UI dialog
 * - Custom styled form controls
 * - Icon indicators for each filter type
 * 
 * Filter Types:
 * - ALL: Show all movements (no filtering)
 * - SPECIFIC_DATE: Filter by single date
 * - SPECIFIC_MONTH: Filter by month and year
 * - SPECIFIC_YEAR: Filter by year only
 * - DATE_RANGE: Filter by start and end dates
 * 
 * Filter Flow:
 * 1. User selects filter type
 * 2. Type-specific inputs appear
 * 3. User sets filter criteria
 * 4. Apply or Print triggers API request
 * 5. Filtered movements returned to parent
 * 
 * API Integration:
 * - Fetches filtered data from server
 * - Uses accounting ID for patient context
 * - Formats dates for API (ISO format)
 * - Handles query parameters for different filter types
 * 
 * Visual Design:
 * - Material-UI theme integration
 * - Icon buttons and chips
 * - Color-coded filter sections
 * - Responsive grid layout
 * - Loading spinner during fetch
 * 
 * @param {PrintFiltersModalProps} props - Component properties
 * @returns {JSX.Element} Filter modal dialog
 */
const PrintFiltersModal: React.FC<PrintFiltersModalProps> = ({
  open,
  onClose,
  onApplyFilters,
  movements,
  accountingId,
  t,
}) => {
  const theme = useTheme();
  const [filters, setFilters] = useState<DateTypeFiltersInterface>({
    type: DateFilterTypeEnum.ALL,
  });
  const [isLoading, error, sendRequest] = useHttp();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const controlStyles = {
    '& .MuiInputBase-root': {
      minHeight: '56px',
      height: '56px',
    },
    '& .MuiSelect-select': {
      paddingTop: '16px',
      paddingBottom: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiInputLabel-root': {
      top: '-2px',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  };

  const datePickerStyles = {
    '& .MuiInputBase-root': {
      minHeight: '56px',
      height: '56px',
    },
    '& .MuiInputBase-input': {
      paddingTop: '16px',
      paddingBottom: '16px',
    },
    '& .MuiInputLabel-root': {
      top: '-2px',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  };

  useEffect(() => {
    if (filters.type === DateFilterTypeEnum.SPECIFIC_MONTH && (filters.selectedMonth === undefined || filters.selectedYear === undefined)) {
      setFilters(prev => ({
        ...prev,
        selectedMonth: prev.selectedMonth ?? currentMonth,
        selectedYear: prev.selectedYear ?? currentYear,
      }));
    } else if (filters.type === DateFilterTypeEnum.SPECIFIC_YEAR && filters.selectedYear === undefined) {
      setFilters(prev => ({
        ...prev,
        selectedYear: prev.selectedYear ?? currentYear,
      }));
    }
  }, [filters.type, currentMonth, currentYear]);

  const getYearRange = () => {
    if (movements.length === 0) return { minYear: currentYear, maxYear: currentYear };
    
    const years = movements.map(m => new Date(m.date).getFullYear());
    return {
      minYear: Math.min(...years),
      maxYear: Math.max(...years),
    };
  };

  const { minYear, maxYear } = getYearRange();

  const handleFilterChange = (field: keyof DateTypeFiltersInterface, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApply = () => {   
    const searchParams: SearchParamsInterface = {};

    switch (filters.type) {
      case DateFilterTypeEnum.ALL:
        onApplyFilters({ ...searchParams, type: DateFilterTypeEnum.ALL }, movements);
        return;
      
      case DateFilterTypeEnum.SPECIFIC_DATE:
        if (filters.date) {
          searchParams.startDate = createSafeDate(filters.date, false);
          searchParams.endDate = createSafeDate(filters.date, false);
        }
        break;
      
      case DateFilterTypeEnum.SPECIFIC_MONTH:
        const month = filters.selectedMonth ?? currentMonth;
        const year = filters.selectedYear ?? currentYear;
        searchParams.selectedMonth = Number(month);
        searchParams.selectedYear = Number(year);
        break;
      
      case DateFilterTypeEnum.SPECIFIC_YEAR:
        const yearValue = filters.selectedYear ?? currentYear;
        searchParams.selectedYear = Number(yearValue);
        break;
      
      case DateFilterTypeEnum.DATE_RANGE:
        if (filters.startDate && filters.endDate) {
          if (new Date(filters.startDate) > new Date(filters.endDate)) {
            return;
          }
          
          searchParams.startDate = createSafeDate(filters.startDate, false);
          searchParams.endDate = createSafeDate(filters.endDate, false);
        }
        break;
    }

    if (filters.type.toString() !== DateFilterTypeEnum.ALL.toString()) {
      fetchFilteredMovements(searchParams);
    }
  };

  const fetchFilteredMovements = (searchParams: SearchParamsInterface) => {
    const endpoint = `${CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS}/all`;
    const queryParams = new URLSearchParams();
    
    const formatDate = (date: Date): string => {
      if (formatDateForAPI) {
        return formatDateForAPI(date);
      }
      return date.toISOString();
    };

    try {
      if (searchParams.selectedMonth !== undefined) {
        const month = Number(searchParams.selectedMonth);
        if (!isNaN(month) && month >= 0 && month <= 11) {
          queryParams.append('selectedMonth', month.toString());
        }
      }
      
      if (searchParams.selectedYear !== undefined) {
        const year = Number(searchParams.selectedYear);
        if (!isNaN(year) && year >= 1900 && year <= 2100) {
          queryParams.append('selectedYear', year.toString());
        }
      }
      
      if (searchParams.startDate) {
        const formattedStartDate = formatDate(searchParams.startDate);
        queryParams.append('startDate', formattedStartDate);
      }
      
      if (searchParams.endDate) {
        const formattedEndDate = formatDate(searchParams.endDate);
        queryParams.append('endDate', formattedEndDate);
      }

      const fullEndpoint = queryParams.toString() ? `${endpoint}?${queryParams.toString()}` : endpoint;

      sendRequest<QueryResultInterface<AccountingMovementInterface>, void>(
        fullEndpoint,
        (result) => {
          const movementsArray = Array.isArray(result) ? result : result.results || [];
          onApplyFilters({ ...searchParams, type: filters.type }, movementsArray);
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accountingId }],
       
      );
    } catch (error) {
      console.error('Error preparing request:', error);
    }
  };

  const handleReset = () => {
    setFilters({ type: DateFilterTypeEnum.ALL });
  };

  const isValidFilter = (): boolean => {
    switch (filters.type) {
      case DateFilterTypeEnum.ALL:
        return true;
      case DateFilterTypeEnum.SPECIFIC_DATE:
        return !!filters.date;
      case DateFilterTypeEnum.SPECIFIC_MONTH:
        const month = filters.selectedMonth ?? currentMonth;
        const year = filters.selectedYear ?? currentYear;
        return month !== undefined && year !== undefined && 
               month >= 0 && month <= 11 && 
               year >= 1900 && year <= 2100;
      case DateFilterTypeEnum.SPECIFIC_YEAR:
        const yearValue = filters.selectedYear ?? currentYear;
        return yearValue !== undefined && yearValue >= 1900 && yearValue <= 2100;
      case DateFilterTypeEnum.DATE_RANGE:
        return !!(new Date(filters.startDate) <= new Date(filters.endDate));
      default:
        return false;
    }
  };

  const filterTypeOptions = [
    { 
      value: DateFilterTypeEnum.ALL, 
      label: t("accountingMovements.allMovements"),
      icon: <FilterList />,
      description: t("accountingMovements.allMovementsDesc"),
    },
    { 
      value: DateFilterTypeEnum.SPECIFIC_DATE, 
      label: t("accountingMovements.specificDate"),
      icon: <Today />,
      description: t("accountingMovements.specificDateDesc"),
    },
    { 
      value: DateFilterTypeEnum.SPECIFIC_MONTH, 
      label: t("accountingMovements.specificMonth"),
      icon: <CalendarToday />,
      description: t("accountingMovements.specificMonthDesc"),
    },
    { 
      value: DateFilterTypeEnum.SPECIFIC_YEAR, 
      label: t("accountingMovements.specificYear"),
      icon: <Schedule />,
      description: t("accountingMovements.specificYearDesc"),
    },
    { 
      value: DateFilterTypeEnum.DATE_RANGE, 
      label: t("accountingMovements.dateRange"),
      icon: <DateRange />,
      description: t("accountingMovements.dateRangeDesc"),
    },
  ];

  const months = [
    { value: MonthEnum.JANUARY, label: t("months.january") },
    { value: MonthEnum.FEBRUARY, label: t("months.february") },
    { value: MonthEnum.MARCH, label: t("months.march") },
    { value: MonthEnum.APRIL, label: t("months.april") },
    { value: MonthEnum.MAY, label: t("months.may") },
    { value: MonthEnum.JUNE, label: t("months.june") },
    { value: MonthEnum.JULY, label: t("months.july") },
    { value: MonthEnum.AUGUST, label: t("months.august") },
    { value: MonthEnum.SEPTEMBER, label: t("months.september") },
    { value: MonthEnum.OCTOBER, label: t("months.october") },
    { value: MonthEnum.NOVEMBER, label: t("months.november") },
    { value: MonthEnum.DECEMBER, label: t("months.december") },
  ];

  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: '#fff',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        backgroundColor: ' #1a1a1a',
        color: '#fff',
        borderRadius: '8px 8px 0 0',
        position: 'relative'
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <FilterList />
            <Typography variant="h6"  color={'#fff'}>
              {t("accountingMovements.selectPrintFilter")}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.23)' }
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              {t("accountingMovements.filterType")}
            </Typography>
            
            <Autocomplete
              options={filterTypeOptions}
              getOptionLabel={(option) => option.label}
              value={filterTypeOptions.find(opt => opt.value === filters.type) || filterTypeOptions[0]}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleFilterChange('type', newValue.value);
                }
              }}
              disabled={isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  fullWidth
                  sx={controlStyles}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Box sx={{ color: theme.palette.primary.main }}>
                      {option.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6">
                        {option.label}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            />
          </Box>

          <Divider sx={{ my: 3 }} />
          {filters.type !== DateFilterTypeEnum.ALL && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                {t("accountingMovements.filterConfiguration")}
              </Typography>
              
              {filters.type === DateFilterTypeEnum.SPECIFIC_DATE && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={datePickerStyles}>
                      <DatePicker
                        label={t("accountingMovements.selectDate")}
                        value={filters.date || null}
                        onChange={(date) => handleFilterChange('date', date)}
                        disabled={isLoading}
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}
              
              {filters.type === DateFilterTypeEnum.SPECIFIC_MONTH && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={controlStyles}>
                      <InputLabel>{t("accountingMovements.month")}</InputLabel>
                      <Select
                        value={filters.selectedMonth ?? currentMonth}
                        label={t("accountingMovements.month")}
                        onChange={(e) => handleFilterChange('selectedMonth', Number(e.target.value))}
                        disabled={isLoading}
                      >
                        {months.map((month) => (
                          <MenuItem key={month.value} value={month.value}>
                            {month.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={controlStyles}>
                      <InputLabel>{t("accountingMovements.year")}</InputLabel>
                      <Select
                        value={filters.selectedYear ?? currentYear}
                        label={t("accountingMovements.year")}
                        onChange={(e) => handleFilterChange('selectedYear', Number(e.target.value))}
                        disabled={isLoading}
                      >
                        {years.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              
              {filters.type === DateFilterTypeEnum.SPECIFIC_YEAR && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={controlStyles}>
                      <InputLabel>{t("accountingMovements.year")}</InputLabel>
                      <Select
                        value={filters.selectedYear ?? currentYear}
                        label={t("accountingMovements.year")}
                        onChange={(e) => handleFilterChange('selectedYear', Number(e.target.value))}
                        disabled={isLoading}
                      >
                        {years.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              
              {filters.type === DateFilterTypeEnum.DATE_RANGE && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={datePickerStyles}>
                      <DatePicker
                        label={t("accountingMovements.startDate")}
                        value={filters.startDate || null}
                        onChange={(date) => handleFilterChange('startDate', date)}
                        disabled={isLoading}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={datePickerStyles}>
                      <DatePicker
                        label={t("accountingMovements.endDate")}
                        value={filters.endDate || null}
                        onChange={(date) => handleFilterChange('endDate', date)}
                        disabled={isLoading}
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa'}}>
        <Button 
          onClick={onClose} 
          color="secondary" 
          disabled={isLoading}
          variant="contained"
        >
          {t("common.cancel")}
        </Button>
        
        <Button 
          type="button"
          onClick={handleReset} 
          variant="contained"
          disabled={isLoading}
          startIcon={<Clear />}
          color="dark"
        >
          {t("common.resetFilters")}
        </Button>
        
        <Button 
          onClick={handleApply} 
          color="info" 
          variant="contained"
          disabled={!isValidFilter() || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Print />}
        >
          {isLoading ? t("common.loading") : t("accountingMovements.printWithFilter")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintFiltersModal;